import React, { useState, useRef, useEffect } from "react";
import "./FeaturedEpisodeSection.css";
import episodes from "./episodesData.js";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

function FeaturedEpisodeSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentEpisode, setCurrentEpisode] = useState(episodes[0]);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(50);
  const audioRef = useRef(new Audio(currentEpisode.audioSrc));
  const [currentTime, setCurrentTime] = useState("00:00");
  const [totalDuration, setTotalDuration] = useState("00:00");
  const [currentPage, setCurrentPage] = useState(1);
  const episodesPerPage = 3;
  const indexOfLastEpisode = currentPage * episodesPerPage;
  const indexOfFirstEpisode = indexOfLastEpisode - episodesPerPage;
  const currentEpisodes = episodes.slice(
    indexOfFirstEpisode,
    indexOfLastEpisode
  );
  const [isRepeat, setIsRepeat] = useState(false);
  const episodeListRef = useRef(null);

  useEffect(() => {
    audioRef.current.pause();
    audioRef.current = new Audio(currentEpisode.audioSrc);
    audioRef.current.volume = volume / 100;
    if (isPlaying) {
      audioRef.current.play();
    }

    return () => {
      audioRef.current.pause();
      audioRef.current.src = "";
    };
  }, [currentEpisode]);

  useEffect(() => {
    audioRef.current.volume = volume / 100;
  }, [volume]);

  useEffect(() => {
    const audio = audioRef.current;

    const updateProgress = () => {
      if (audio.duration) {
        setCurrentTime(formatTime(audio.currentTime));
        setTotalDuration(formatTime(audio.duration));
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const handleEnded = () => {
      if (isRepeat) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      } else {
        setIsPlaying(false);
      }
    };

    const setAudioDuration = () => {
      setTotalDuration(formatTime(audio.duration));
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("loadedmetadata", setAudioDuration);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("loadedmetadata", setAudioDuration);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [currentEpisode, isPlaying, isRepeat]);

  const goToPreviousEpisode = () => {
    const currentIndex = episodes.findIndex(
      (ep) => ep.id === currentEpisode.id
    );
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentEpisode(episodes[newIndex]);
      setIsPlaying(true);
      setCurrentPage(Math.floor(newIndex / episodesPerPage) + 1);
    }
  };

  const goToNextEpisode = () => {
    const currentIndex = episodes.findIndex(
      (ep) => ep.id === currentEpisode.id
    );
    if (currentIndex < episodes.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentEpisode(episodes[newIndex]);
      setIsPlaying(true);
      setCurrentPage(Math.floor(newIndex / episodesPerPage) + 1);
    }
  };

  const handleSeek = (e) => {
    const newProgress = e.target.value;
    setProgress(newProgress);
    audioRef.current.currentTime =
      (newProgress / 100) * audioRef.current.duration;
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const changeEpisode = (episode) => {
    if (currentEpisode.id !== episode.id) {
      setCurrentEpisode(episode);
      setIsPlaying(true);
    }
  };

  const playRandomEpisode = () => {
    const randomIndex = Math.floor(Math.random() * episodes.length);
    const randomEpisode = episodes[randomIndex];
    setCurrentEpisode(randomEpisode);
    setIsPlaying(true);
    setCurrentPage(Math.floor(randomIndex / episodesPerPage) + 1);
  };

  return (
    <div className="featured-container">
      <p className="featured-header">
        Step into a world of leadership excellence with these standout episodes
        and curated picks.
      </p>

      <div className="featured-card">
        <div className="featured-card-content">
          <div className="episode-info">
            <div className="featured-image-wrapper">
              <img
                src={currentEpisode.image}
                alt="Podcast Cover"
                className="featured-cover-image"
              />
              {/* <div className="overlay-icon-wrapper">
                <img
                  src={
                    isPlaying
                      ? "images/Podcast/pause2.png"
                      : "images/Podcast/Play button.png"
                  }
                  alt="Play/Pause Button"
                  className="overlay-play-icon"
                />
              </div> */}
            </div>
            <div className="podcast-top-content-box">
              <h3 className="featured-title">{currentEpisode.title}</h3>
              <p className="featured-date">
                Season 1 | Episode {currentEpisode.id}
              </p>
              <p className="episode-date">{currentEpisode.date}</p>
            </div>
          </div>

          <div className="featured-controls">
            <div className="button-group">
              <button onClick={goToPreviousEpisode} className="control-button">
                <img
                  src="images/Podcast/fastBackward.png"
                  alt="Rewind"
                  className="control-icon"
                />
              </button>

              <button className="control-button" onClick={playRandomEpisode}>
                <img
                  src="images/Podcast/Icon open-random.png"
                  alt="Shuffle"
                  className="control-icon shuffleRepeat"
                />
              </button>
            </div>
            <button onClick={togglePlayPause} className="play-button">
              <img
                src={
                  isPlaying
                    ? "images/Podcast/pause-circle.jpg"
                    : "images/Podcast/PlayButton-new.png"
                }
                alt="Play/Pause"
                className={`control-icon ${isPlaying ? "invert-icon" : ""}`}
              />
            </button>

            <div className="secondary-button-group">
              <button onClick={goToNextEpisode} className="control-button">
                <img
                  src="images/Podcast/fastforward.png"
                  alt="Forward"
                  className="control-icon"
                />
              </button>

              <button
                className="control-button"
                onClick={() => setIsRepeat(!isRepeat)}
              >
                <img
                  src="images/Podcast/Icon metro-loop.png"
                  alt="Repeat"
                  className="control-icon shuffleRepeat"
                />
              </button>
            </div>

            {/* <img
              src="images/Podcast/frequency.png"
              alt="Podcast Image"
              className="additional-icon"
            /> */}
          </div>

          <div className="progress-section">
            <div className="progress-icons">
              <button className="icon-button">
                <img
                  src="images/Podcast/Icon heart-empty.png"
                  alt="Like"
                  className="action-icon"
                />
              </button>
              <button
                className="icon-button"
                onClick={() => {
                  episodeListRef.current?.scrollIntoView({
                    behavior: "smooth",
                  });
                }}
              >
                <img
                  src="images/Podcast/Icon queue-music.png"
                  alt="Add to Playlist"
                  className="action-icon"
                />
              </button>
            </div>

            <div className="volume-container">
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                className="volume-slider"
                aria-label="slider"
              />
              <img
                src="images/Podcast/feather-volume-2.png"
                className="volume-icon"
                alt="Volume Icon"
              />
            </div>

            <div className="progress-bar-container">
              <span className="time">
                {currentTime} / {totalDuration}
              </span>
              <div className="progress-wrapper">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={handleSeek}
                  className="progress-slider"
                  aria-label="progress"
                />
              </div>
            </div>

            <div className="file-size">
              <img
                src="images/Podcast/sd-card-new.png"
                alt="SD Card Icon"
                className="file-icon"
              />
              <span className="file-text">{currentEpisode.fileSize}</span>
            </div>
          </div>
        </div>

        <div className="episode-details">
          <h3 className="brought-by">
            Brought to you by Learning Experiences L&TT ATCI
          </h3>
          <p
            className="featured-description"
            dangerouslySetInnerHTML={{ __html: currentEpisode.description }}
          ></p>
          <p
            className="featured-guest-info"
            dangerouslySetInnerHTML={{ __html: currentEpisode.guestInfo }}
          ></p>
        </div>
        {/* <ul className="ReferenceLinkText">
          <li>Reference Links:</li>
          <li className="reference-links-sub">
            <a href="#" target="_blank">
              Series Submission Template
            </a>
            <span>|</span>
            <a href="#" target="_blank">
              Content Creator Guidelines
            </a>
            <span>|</span>
            <a href="#" target="_blank">
              Promotion Checklist
            </a>
          </li>
        </ul> */}
        <p className="ReferenceLinkText">
          Reference Links:{" "}
          <a
            href="https://ts.accenture.com/:p:/r/sites/Mobile_App_Management/podcasts/_layouts/15/guestaccess.aspx?share=ESIYkbHLxzJDjWk4mI_dGUIB4oLn53Sn4VWCmS8KmRfCAw"
            target="_blank"
          >
            Series Submission Template
          </a>
          <span> | </span>
          <a
            href="https://ts.accenture.com/sites/brandspace/accessibility_guidelines"
            target="_blank"
          >
            Content Creator Guidelines
          </a>
          <span> | </span>
          <a
            href="https://ts.accenture.com/sites/BrandSpace/DownloadFiles/Podcast_Guidebook/Podcast_promotion%20checklist.pdf"
            target="_blank"
          >
            Promotion Checklist
          </a>
        </p>
      </div>

      <div className="featured-episode-list" ref={episodeListRef}>
        <div className="episode-information">
          <h2 className="lorem-header">Tune In to Enriching Episodes</h2>
          <p className="lorem-description">
            Discover conversations that explore trends, insights, and stories
            across learning, technology, leadership, and more. These episodes
            are packed with ideas and voices that spark inspiration.
          </p>
          <div className="pagination">
            <button
              className="prev"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <FaArrowLeft
                color={currentPage === 1 ? "#ccc" : "#000"} // Gray if disabled, black otherwise
              />
            </button>
            <button
              className="next"
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(prev + 1, Math.ceil(episodes.length / episodesPerPage))
                )
              }
              disabled={currentPage === Math.ceil(episodes.length / episodesPerPage)}
            >
              <FaArrowRight
                color={
                  currentPage === Math.ceil(episodes.length / episodesPerPage)
                    ? "#ccc"
                    : "#000"
                }
              />
            </button>

            {/* Page number buttons - currently empty */}
            {[].map((page) => (
              <button
                key={page}
                className={`page ${currentPage === page ? "active" : ""}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
          </div>
          
        </div>

        <div className="episode-scroll-wrapper">
          {/* <div className="episode-bg-layer">
    <img src="images/Podcast/frequency.png" alt="Decorative" />
  </div> */}
          {currentEpisodes.map((ep, idx) => {
            const isCurrent = currentEpisode.id === ep.id;
            return (
              <div
                key={ep.id}
                onClick={() => changeEpisode(ep)}
                className={`episode-item ${isCurrent ? "active" : "dimmed"}`}
              >
                <div className="episode-image-wrapper">
                  <img
                    src={ep.image}
                    alt={ep.title}
                    className="episode-image"
                  />
                  <div className="episode-overlay-icon">
                    <img
                      src={
                        isCurrent && isPlaying
                          ? "images/Podcast/pause2.png"
                          : "images/Podcast/Play button.png"
                      }
                      alt="Play/Pause Icon"
                      className="overlay-play-icon"
                    />
                  </div>
                </div>
                <p className="featured-date">
                  Episode {indexOfFirstEpisode + idx + 1}
                </p>
                <p className="episode-title">{ep.title}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default FeaturedEpisodeSection;
